import React from 'react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  fetchComponents,
  createComponentAsync,
  updateComponentAsync,
  deleteComponentAsync,
  createBlankRecipe
} from '../../store/slices/recipesSlice';
import { RecipeCard } from './components/RecipeCard';
import { Ingredient, Recipe } from '../../types/recipe';

export const Components: React.FC = () => {
  const dispatch = useAppDispatch();
  const components = useAppSelector(state => state.recipes.components);
  const loading = useAppSelector(state => state.recipes.componentsLoading);
  const error = useAppSelector(state => state.recipes.componentsError);

  const handleCreateRecipe = async () => {
    // Generate default component code based on current count
    const nextNumber = components.length + 1;
    const defaultComponentCode = `COMP-${String(nextNumber).padStart(3, '0')}`;

    const newRecipe = {
      ...createBlankRecipe('New Component'),
      productCode: defaultComponentCode
    };

    try {
      await dispatch(createComponentAsync(newRecipe)).unwrap();
    } catch (error) {
      console.error('Failed to create component:', error);
    }
  };

  const handleUpdateName = async (id: string, name: string) => {
    const component = components.find(c => c.id === id);
    if (component) {
      await dispatch(updateComponentAsync({ ...component, name }));
    }
  };

  const handleUpdateYield = async (id: string, yieldValue: number) => {
    const component = components.find(c => c.id === id);
    if (component) {
      await dispatch(updateComponentAsync({ ...component, yield: yieldValue }));
    }
  };

  const handleUpdateIngredients = async (recipeId: string, ingredients: Ingredient[]) => {
    const component = components.find(c => c.id === recipeId);
    if (component) {
      await dispatch(updateComponentAsync({ ...component, ingredients }));
    }
  };

  const handleUpdateProductCode = async (id: string, productCode: string) => {
    const component = components.find(c => c.id === id);
    if (component) {
      try {
        await dispatch(updateComponentAsync({ ...component, productCode })).unwrap();
      } catch (error) {
        console.error('Failed to update component code:', error);
      }
    }
  };

  const handleDeleteComponent = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this component?')) {
      try {
        await dispatch(deleteComponentAsync(id)).unwrap();
      } catch (error) {
        console.error('Failed to delete component:', error);
      }
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="container" style={{ padding: '20px', textAlign: 'center' }}>
        <p>Loading components...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="container" style={{ padding: '20px', textAlign: 'center' }}>
        <p style={{ color: 'red' }}>Error: {error}</p>
        <button onClick={() => dispatch(fetchComponents())}>Retry</button>
      </div>
    );
  }

  return (
    <div className="container" style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ marginBottom: '20px' }}>
        <a href="/" style={{ textDecoration: 'none', color: '#4CAF50' }}>
          ← Back to Recipes
        </a>
      </div>

      <header style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '2rem', color: '#333' }}>Components</h1>
      </header>

      <div id="recipes-list" className="recipes-list">
        {components.map((recipe: Recipe) => (
          <RecipeCard
            key={recipe.id}
            recipe={recipe}
            components={[]} // Components can't reference other components
            onUpdateName={handleUpdateName}
            onUpdateYield={handleUpdateYield}
            onUpdateIngredients={handleUpdateIngredients}
            onDelete={handleDeleteComponent}
            onUpdateProductCode={handleUpdateProductCode}
            showProductCode={true}
          />
        ))}
      </div>

      <button
        type="button"
        id="create-recipe-btn"
        className="create-recipe-btn create-recipe-btn-bottom"
        onClick={handleCreateRecipe}
        style={{
          padding: '12px 24px',
          backgroundColor: '#4CAF50',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
          fontSize: '1rem',
          fontWeight: 'bold',
          marginTop: '20px'
        }}
      >
        + Create New Recipe
      </button>
    </div>
  );
};
